/**
 * Custom fetch mutator for orval (httpClient: 'fetch' mode).
 *
 * Orval calls this with the standard fetch signature:
 *   `cguardFetcher<TResponse>(url: string, init: RequestInit)`
 *
 * The generated TResponse is the orval wrapped shape:
 *   `{ data: TBody; status: number; headers: Headers }`
 *
 * Application code does NOT call this directly and does NOT consume the
 * generated hooks raw — thin wrapper hooks in `src/hooks/` unwrap to the
 * body type via the TanStack Query `select` option.
 *
 * Cross-cutting concerns (base URL, auth header, error shape, telemetry)
 * live here so SPAs do not have to thread anything through call sites.
 *
 * Configure once at app boot:
 *   `configureCguardClient({ baseUrl, getAccessToken })`
 */

export interface CguardClientConfig {
  /** Base URL for the central API. Same-origin in dev (Vite proxy + msw). */
  baseUrl: string;
  /** Returns the current bearer token (e.g. from Keycloak). Called per request. */
  getAccessToken?: () => string | null | undefined;
  /** Optional per-request hook (telemetry, breadcrumb, etc.). */
  onRequest?: (info: { url: string; method: string }) => void;
}

let config: CguardClientConfig = { baseUrl: '' };

export function configureCguardClient(next: CguardClientConfig): void {
  config = { ...config, ...next };
}

export function getCguardClientConfig(): Readonly<CguardClientConfig> {
  return config;
}

export class CguardApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  readonly headers: Headers;
  constructor(status: number, message: string, body: unknown, headers?: Headers) {
    super(message);
    this.name = 'CguardApiError';
    this.status = status;
    this.body = body;
    this.headers = headers ?? new Headers();
  }
}

function joinUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const base = config.baseUrl ?? '';
  if (!base) return path;
  if (base.endsWith('/') && path.startsWith('/')) return base + path.slice(1);
  if (!base.endsWith('/') && !path.startsWith('/')) return `${base}/${path}`;
  return base + path;
}

function requestHeaders(
  init: RequestInit,
  defaultAccept: string,
  authorization: 'central' | 'anonymous' | 'caller' = 'central',
): Headers {
  const headers = new Headers(init.headers ?? {});
  if (authorization === 'anonymous') headers.delete('authorization');
  if (!headers.has('accept')) headers.set('accept', defaultAccept);
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const token = authorization === 'central' ? config.getAccessToken?.() : null;
  if (token) headers.set('authorization', `Bearer ${token}`);
  return headers;
}

function isBinaryContentType(contentType: string): boolean {
  return (
    contentType.startsWith('application/octet-stream') ||
    contentType.startsWith('image/') ||
    contentType.startsWith('audio/') ||
    contentType.startsWith('video/')
  );
}

async function readResponseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;

  const contentType = res.headers.get('content-type')?.toLowerCase() ?? '';
  if (isBinaryContentType(contentType)) return res.blob();

  const text = await res.text();
  if (text.length === 0) return undefined;
  if (!contentType.includes('json') && contentType.length > 0) return text;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function fetchResponse<T>(
  url: string,
  init: RequestInit,
  authorization: 'central' | 'anonymous' | 'caller',
  useConfiguredBase = true,
): Promise<T> {
  const fullUrl = useConfiguredBase ? joinUrl(url) : url;
  const method = (init.method ?? 'GET').toUpperCase();
  config.onRequest?.({ url: fullUrl, method });

  const headers = requestHeaders(init, 'application/json', authorization);
  const res = await fetch(fullUrl, { ...init, headers });
  const data = await readResponseBody(res);

  if (!res.ok) {
    throw new CguardApiError(
      res.status,
      `HTTP ${res.status} on ${method} ${fullUrl}`,
      data,
      res.headers,
    );
  }

  return { data, status: res.status, headers: res.headers } as T;
}

export async function cguardFetcher<T>(url: string, init: RequestInit): Promise<T> {
  return fetchResponse<T>(url, init, 'central');
}

/**
 * Load a complete collection from an endpoint that may return either the
 * legacy flat array or Central's `{ items, total }` paged envelope.
 */
export async function fetchAllPagedItems<T>(
  loadPage: (page: number, pageSize: number) => Promise<unknown>,
  normalise: (item: unknown) => T,
  pageSize = 200,
): Promise<T[]> {
  const result: T[] = [];

  for (let page = 1; ; page += 1) {
    const body = await loadPage(page, pageSize);
    if (Array.isArray(body)) return body.map(normalise);
    if (!body || typeof body !== 'object') {
      throw new Error('Expected a paged response.');
    }

    const envelope = body as { items?: unknown; total?: unknown };
    if (!Array.isArray(envelope.items)) {
      throw new Error('Expected a paged response with an items array.');
    }

    result.push(...envelope.items.map(normalise));
    const total =
      typeof envelope.total === 'number' && Number.isFinite(envelope.total)
        ? Math.max(0, envelope.total)
        : undefined;

    if (envelope.items.length < pageSize || (total !== undefined && result.length >= total)) {
      return result;
    }
  }
}

/**
 * JSON request through the configured central base URL without the current
 * browser access token. Use only for endpoints whose protocol is explicitly
 * anonymous, such as the legacy field-panel alarm receiver.
 */
export async function cguardAnonymousFetcher<T>(url: string, init: RequestInit): Promise<T> {
  return fetchResponse<T>(url, init, 'anonymous');
}

/**
 * JSON transport for an explicit, isolated origin with a caller-owned
 * credential. It never joins the Central base URL and never injects the
 * current user's Central access token.
 */
export async function cguardIsolatedFetcher<T>(url: string, init: RequestInit): Promise<T> {
  if (!/^https?:\/\//i.test(url)) {
    throw new Error('An isolated request requires an absolute HTTP(S) URL.');
  }
  return fetchResponse<T>(url, init, 'caller', false);
}

export interface CguardDownloadResponse {
  blob: Blob;
  status: number;
  headers: Headers;
}

export async function cguardBlobFetcher(url: string, init: RequestInit = {}): Promise<Blob> {
  const fullUrl = joinUrl(url);
  const method = (init.method ?? 'GET').toUpperCase();
  config.onRequest?.({ url: fullUrl, method });

  const headers = requestHeaders(init, 'application/octet-stream');
  const res = await fetch(fullUrl, { ...init, headers });
  if (!res.ok) {
    throw new CguardApiError(
      res.status,
      `HTTP ${res.status} on ${method} ${fullUrl}`,
      await readResponseBody(res),
      res.headers,
    );
  }

  // Download callers need Blob semantics even for textual formats such as CSV.
  return res.blob();
}

/**
 * Authenticated download transport retaining response metadata for callers
 * that must validate integrity and disposition headers before saving bytes.
 */
export async function cguardDownloadFetcher(
  url: string,
  init: RequestInit = {},
): Promise<CguardDownloadResponse> {
  const fullUrl = joinUrl(url);
  const method = (init.method ?? 'GET').toUpperCase();
  config.onRequest?.({ url: fullUrl, method });

  const headers = requestHeaders(init, 'application/octet-stream');
  const res = await fetch(fullUrl, { ...init, headers });
  if (!res.ok) {
    throw new CguardApiError(
      res.status,
      `HTTP ${res.status} on ${method} ${fullUrl}`,
      await readResponseBody(res),
      res.headers,
    );
  }

  return { blob: await res.blob(), status: res.status, headers: res.headers };
}

export default cguardFetcher;

/** Authenticated streaming response; callers own cancellation and body consumption. */
export async function cguardStreamFetcher(url: string, init: RequestInit = {}): Promise<Response> {
  if (!url.startsWith('/v1/') || url.includes('\\')) throw new Error('Expected a Central API path.');
  const response = await fetch(joinUrl(url), { ...init, headers: requestHeaders(init, '*/*'), redirect: 'error' });
  if (!response.ok) throw new CguardApiError(response.status, `HTTP ${response.status}`, await readResponseBody(response), response.headers);
  return response;
}
